import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import {
  DbOntologyColumn,
  useCreateDbOntologyColumn,
  useCreateDbOntologyConstraint,
  useDeleteDbOntologyConstraint,
  useGetDbOntologyConstraints,
  useUpdateDbOntologyNode,
} from "@/service-hooks/use-get-db-ontology-service";

interface DbOntologyColumnDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectUuid: string;
  dataLakeId: string;
  tableId: string;
  tableLabel?: string;
  existingColumnsCount?: number;
  onCreated?: () => void;
  // the column to edit; create mode when absent
  column?: DbOntologyColumn | null;
  isReadOnly?: boolean;
}

interface FormState {
  name: string;
  dataType: string;
  length: string;
  nullable: boolean;
  primaryKey: boolean;
  comment: string;
}

// "VARCHAR(255)" → { dataType: "VARCHAR", length: "255" }: the list shows the type with its length folded in
const splitType = (raw: string | undefined): { dataType: string; length: string } => {
  const t = String(raw ?? "").trim();
  const match = t.match(/^([^()\s]+)\s*\((\d+)\)\s*$/);
  return match ? { dataType: match[1], length: match[2] } : { dataType: t, length: "" };
};

const EMPTY: FormState = {
  name: "",
  dataType: "",
  length: "",
  nullable: false,
  primaryKey: false,
  comment: "",
};

export default function DbOntologyColumnDialog({
  isOpen,
  onClose,
  projectUuid,
  dataLakeId,
  tableId,
  tableLabel,
  existingColumnsCount = 0,
  onCreated,
  column,
  isReadOnly = false,
}: DbOntologyColumnDialogProps) {
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const createColumn = useCreateDbOntologyColumn();
  const updateNode = useUpdateDbOntologyNode();
  const createConstraint = useCreateDbOntologyConstraint();
  const deleteConstraint = useDeleteDbOntologyConstraint();
  const isEdit = !!column?.id;
  const { refetch: refetchConstraints } = useGetDbOntologyConstraints(
    dataLakeId || undefined,
    isOpen && isEdit && !!tableId,
    { projectUuid, tableId: tableId || undefined },
  );
  const isPending = createColumn.isPending || saving;

  useEffect(() => {
    if (isOpen) {
      setForm(
        column
          ? {
              name: column.name ?? "",
              ...splitType(column.type),
              nullable: !!column.nullable,
              primaryKey: !!column.primaryKey,
              comment: column.description ?? "",
            }
          : { ...EMPTY },
      );
    }
  }, [isOpen, column]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // A primary key column is always NOT NULL; the backend rejects the pair too.
  const pkNullableConflict = form.primaryKey && form.nullable;

  // A column keeps its own type, length, nullability and comment; primary-key membership lives on the table's
  // PRIMARY_KEY constraint (the column's flag is derived from it), so switching it adds or removes the column there.
  const saveEdit = async (lengthNum: number | undefined) => {
    if (!column?.id) return;
    await updateNode.mutateAsync({
      label: "DDLColumn",
      projectUuid,
      id: column.id,
      dataLakeId,
      data: {
        ...(form.dataType.trim() ? { dataType: form.dataType.trim() } : {}),
        ...(lengthNum !== undefined && Number.isFinite(lengthNum) && lengthNum > 0 ? { length: lengthNum } : {}),
        nullable: form.nullable,
        comment: form.comment.trim(),
      },
    });

    const wantPrimaryKey = form.primaryKey;
    if (wantPrimaryKey === !!column.primaryKey || !tableId) return;
    // read the table's primary key fresh: moving it between columns is two saves, and this one must see the last
    const fresh = await refetchConstraints();
    const pk = (fresh.data ?? []).find((c) => c.constraintType === "PRIMARY_KEY");
    const pkColumns = pk?.columns ?? [];
    if (wantPrimaryKey) {
      if (pkColumns.includes(column.name)) return;
      if (pk?.id) {
        await updateNode.mutateAsync({
          label: "DDLConstraint", projectUuid, id: pk.id, dataLakeId,
          data: { columns: [...pkColumns, column.name] },
        });
      } else {
        await createConstraint.mutateAsync({
          projectUuid, dataLakeId, tableId, name: null, constraintType: "PRIMARY_KEY", columns: [column.name],
        });
      }
    } else if (pk?.id) {
      const remaining = pkColumns.filter((c) => c !== column.name);
      if (remaining.length === 0) {
        await deleteConstraint.mutateAsync({ constraintId: pk.id, projectUuid, dataLakeId });
      } else {
        await updateNode.mutateAsync({
          label: "DDLConstraint", projectUuid, id: pk.id, dataLakeId,
          data: { columns: remaining },
        });
      }
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || pkNullableConflict || isReadOnly) return;
    const lengthNum = form.length.trim() ? Number(form.length.trim()) : undefined;
    if (isEdit) {
      setSaving(true);
      try {
        await saveEdit(lengthNum);
        onClose();
      } catch {
        // toast handled in mutation
      } finally {
        setSaving(false);
      }
      return;
    }
    try {
      await createColumn.mutateAsync({
        projectUuid,
        dataLakeId,
        tableId,
        name: form.name.trim(),
        dataType: form.dataType.trim() || undefined,
        ordinalPosition: existingColumnsCount + 1,
        length:
          lengthNum !== undefined && Number.isFinite(lengthNum) && lengthNum > 0
            ? lengthNum
            : undefined,
        nullable: form.nullable,
        isPrimaryKey: form.primaryKey,
        comment: form.comment.trim() || undefined,
      });
      onCreated?.();
      onClose();
    } catch {
      // toast handled in mutation
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isReadOnly ? "View Column" : isEdit ? "Edit Column" : "Add Column"}{tableLabel ? ` — ${tableLabel}` : ""}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure the column fields for the selected table.
          </DialogDescription>
        </DialogHeader>

        <div className={`space-y-4 overflow-y-auto flex-1 pr-1 ${CUSTOM_SCROLLBAR}`}>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">
              Name <span className="text-error">*</span>
            </label>
            <Input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Enter column name"
              readOnly={isReadOnly || isEdit}
              className="bg-surface text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Data Type <span className="text-error">*</span></label>
              <Input
                value={form.dataType}
                onChange={(e) => update("dataType", e.target.value)}
                placeholder="e.g. VARCHAR"
                readOnly={isReadOnly}
                className="bg-surface text-white"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Length</label>
              <Input
                value={form.length}
                onChange={(e) => update("length", e.target.value)}
                placeholder="e.g. 255"
                readOnly={isReadOnly}
                className="bg-surface text-white"
                inputMode="numeric"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={form.primaryKey}
                disabled={isReadOnly}
                onCheckedChange={(checked) => update("primaryKey", checked)}
              />
              <span className="text-sm text-white">Primary Key</span>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.nullable}
                disabled={isReadOnly}
                onCheckedChange={(checked) => update("nullable", checked)}
              />
              <span className="text-sm text-white">Nullable</span>
            </div>
          </div>
          {pkNullableConflict && (
            <p role="alert" className="text-sm text-red-400">
              Primary key columns cannot be nullable. Turn off Nullable, or remove the Primary Key.
            </p>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Comment</label>
            <Textarea
              value={form.comment}
              onChange={(e) => update("comment", e.target.value)}
              placeholder="Optional description for this column"
              readOnly={isReadOnly}
              className="bg-surface text-white min-h-[60px]"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-surface-border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {isReadOnly ? "Close" : "Cancel"}
          </Button>
          {!isReadOnly && (
            <Button
              onClick={handleSubmit}
              disabled={!form.name.trim() || pkNullableConflict || isPending}
              className="bg-primary hover:bg-primary-dark text-white"
            >
              {isPending ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save" : "Create"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
